import { ChangeEvent, useCallback, useRef } from "react";

// MUI
import { Grid, Paper, Skeleton, Typography } from "@mui/material";

// Constants
import actionsList from "@Constants/actionsList";

// Component
import UploadDataButtonWrapper from "./UploadDataButtonWrapper";

// Types
import { IPeriodTreeSelection } from "@/Layouts/components/SideBars/PayPeriodSideBar/interfaces";

// Hooks
import { useAuthorization } from "@Common/hooks/Authorization/Authorization";

import { uploadAdvisorPerformance } from "@/DataPages/api/advisorPerformance/queries";
import { errorLogger } from "@/Common/helpers/logger";
import { useLocationId } from "@Common/hooks/Location/useLocationId.tsx";

const useUploadFileHandler = (locationId: string | undefined, payPeriodId: string | undefined, setIngesting: (value: boolean) => void) => {
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	const resetIngestingState = () => {
		setIngesting(false);
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}
	};

	const handleFileUpload = async (file: File) => {
		if (!locationId || !payPeriodId) {
			errorLogger("Invalid locationId or payPeriodId");
			resetIngestingState();
			return;
		}

		setIngesting(true);
		const FILE_INGESTION_TIMEOUT = 60000; // 60 seconds timeout
		timeoutRef.current = setTimeout(resetIngestingState, FILE_INGESTION_TIMEOUT); // 60 seconds timeout

		try {
			const response = await uploadAdvisorPerformance(locationId, payPeriodId, file);
			if (!response.status) {
				errorLogger(`File upload failed: ${response.message}`);
				resetIngestingState();
			}
		} catch (error) {
			errorLogger(`Error uploading file: ${error}`);
			resetIngestingState();
		}
	};

	return useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			if (!locationId || !payPeriodId) {
				errorLogger("Invalid locationId or payPeriodId");
				return;
			}

			const file = event.target.files?.[0];
			if (!file || file.type !== "text/csv") {
				return;
			}

			handleFileUpload(file);
		},
		[locationId, payPeriodId]
	);
};

interface IAdvisorPerformanceModelHeaderProps {
	selected: IPeriodTreeSelection;
	isUpdatable?: boolean;
	isLoading?: boolean;
	ingesting: boolean;
	setIngesting: (value: boolean) => void;
}

const SkeletonLoader = ({ isUpdatable }: { isUpdatable: boolean }) => (
	<Grid item xs={12}>
		<Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
			<Grid item>
				<Skeleton variant="rectangular" height={25} width={350} className="rounded-md" />
			</Grid>
			{isUpdatable && (
				<Grid item className="pr-0">
					<Grid container direction="row" justifyContent="space-between" spacing={6}>
						<SkeletonItem />
						<SkeletonItem />
					</Grid>
				</Grid>
			)}
		</Grid>
	</Grid>
);

const SkeletonItem = () => (
	<Grid item>
		<Skeleton variant="rectangular" height={30} width={120} className="rounded-md" />
	</Grid>
);

export const AdvisorPerformanceModelHeader = (props: Readonly<IAdvisorPerformanceModelHeaderProps>) => {
	const { selected, isUpdatable = false, isLoading = false, ingesting, setIngesting } = props;
	const locationId = useLocationId();

	// Permissions
	const { useAuth: isAuthorized } = useAuthorization();
	const canUploadAdvisorPerformanceReport = isAuthorized(actionsList.UPLOAD_ADVISOR_PERFORMANCE_REPORT);

	const handleUploadFile = useUploadFileHandler(locationId, selected?.payPeriodId, setIngesting);

	if (isLoading) {
		return <SkeletonLoader isUpdatable={isUpdatable} />;
	}

	if (!selected) {
		return <Grid item xs={12} />;
	}

	return (
		<Grid item>
			<Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
				<Grid item>
					<Typography className="!font-montserrat !font-medium !text-2xl/[1.2] text-default" component="div">
						{selected?.titleExtended} Advisor Performance
					</Typography>
				</Grid>
				<Grid item className="!pr-0">
					<Grid container direction="row" justifyContent="space-between" spacing={6}>
						<Grid item>
							<Paper elevation={0}>
								{isUpdatable && (
									<UploadDataButtonWrapper
										isUpdatable={isUpdatable}
										canUpload={canUploadAdvisorPerformanceReport}
										ingesting={ingesting}
										handleUploadFile={handleUploadFile}
									/>
								)}
							</Paper>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};
